import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { fetchSearchedTestScenarios } from "redux-store/testScenarioReducers/testScenarioActions";
import { Box, Grid, TextField, Chip, Autocomplete, Tooltip, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Menu, MenuItem } from "@mui/material";
import {
    attachPreRequisiteWithSuites,
    detachPreRequisiteFromSuite,
    fetchExistingPreRequisiteWithSuites
} from "redux-store/testSuiteScenarioReducers/testSuiteScenarioActions";
import { useSnackbar } from "contexts/CustomSnackbarContext";
import MoreVertIcon from '@mui/icons-material/MoreVert';

const PrerequisiteScenarios = ({ testSuiteId, projectId }) => {
    const dispatch = useDispatch();
    const { openSnackbar } = useSnackbar();
    const [preRequisiteScenarioList, setPreRequisiteScenarioList] = useState([]);
    const [testScenarioList, setTestScenarioList] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [selectedScenarios, setSelectedScenarios] = useState([]);
    const [selectedScenarioIds, setSelectedScenarioIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedScenario, setSelectedScenario] = useState(null);

    useEffect(() => {
        const fetchScenarios = async () => {
            if (inputValue && projectId) {
                setLoading(true);
                const response = await dispatch(fetchSearchedTestScenarios({
                    projectId: projectId,
                    searchText: inputValue,
                    pageNumber: 1
                }));
                const data = response?.payload;
                if (data) {
                    setTestScenarioList(data.test_scenarios || []);
                }
                setLoading(false);
            } else {
                setTestScenarioList([]);
            }
        };

        const timeoutId = setTimeout(fetchScenarios, 300);
        return () => clearTimeout(timeoutId);
    }, [inputValue]);

    useEffect(() => {
        getPreRequisiteScenarios();
    }, [])

    const getPreRequisiteScenarios = async () => {
        const response = await dispatch(fetchExistingPreRequisiteWithSuites({
            testSuiteId: testSuiteId,
        }));
        if (response?.payload?.test_suite_prerequisites) {
            setPreRequisiteScenarioList(response.payload.test_suite_prerequisites);
        }
    };

    const handleAddScenario = (scenario) => {
        if (scenario && !selectedScenarioIds.includes(scenario.id)) {
            setSelectedScenarioIds(prev => [...prev, scenario.id]);
            setSelectedScenarios(prev => [...prev, scenario]);
        }
    };

    const handleSave = async () => {
        if (selectedScenarioIds.length === 0) {
            openSnackbar({
                message: "Please select at least one scenario",
                severity: "error"
            })
            return;
        }
        const response = await dispatch(attachPreRequisiteWithSuites({
            testSuiteId: testSuiteId,
            testScenarioIds: selectedScenarioIds
        }));
        if (response?.payload) {
            getPreRequisiteScenarios(); // Refresh the list
            setSelectedScenarioIds([]);
            setSelectedScenarios([]);
            openSnackbar({
                message: "Prerequisite scenarios attached successfully",
                severity: "success"
            })
        }
    }

    const handleMenuClick = (event, scenario) => {
        setAnchorEl(event.currentTarget);
        setSelectedScenario(scenario);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedScenario(null);
    };

    const handleOpenInNewTab = () => {
        if (selectedScenario) {
            window.open(`/test-scenarios/${selectedScenario.test_scenario.id}`, '_blank');
        }
        handleMenuClose();
    };

    const handleDetach = async () => {
        if (selectedScenario) {
            const response = await dispatch(detachPreRequisiteFromSuite({
                testSuiteId: testSuiteId,
                testScenarioId: selectedScenario.test_scenario.id
            }));
            if (response?.payload?.success) {
                openSnackbar({
                    message: "Prerequisite scenario detached successfully",
                    severity: "success"
                });
                getPreRequisiteScenarios(); // Refresh the list
            }
        }
        handleMenuClose();
    };

    return (
        <Box sx={{ p: 2, backgroundColor: "var(--background-primary)" }}>
            <Grid container alignItems="center" sx={{ mb: 2 }}>
                <h5>Prerequisite Scenarios</h5>
            </Grid>

            {/* Search and Add New Prerequisites */}
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'var(--text-secondary)' }}>
                Add New Prerequisites
            </Typography>
            <Grid container alignItems="center" sx={{ mb: 2 }}>
                <Autocomplete
                    options={testScenarioList}
                    getOptionLabel={(option) => option ? `${option.code} - ${option.title}` : ''}
                    filterOptions={(x) => x}
                    fullWidth
                    inputValue={inputValue}
                    onInputChange={(event, newValue) => setInputValue(newValue)}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            handleAddScenario(newValue);
                            setInputValue("");
                        }
                    }}
                    value={null}
                    loading={loading}
                    noOptionsText="Type Test Scenario Code/Title to Search"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            color="primary"
                            placeholder="Search Test Scenarios"
                            fullWidth
                            size="small"
                            sx={{
                                width: "100%",
                                input: {
                                    backgroundColor: "var(--background-secondary)",
                                    color: "var(--text-primary)",
                                    padding: '6px 10px',
                                },
                                label: { color: '#aaa' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'var(--borderColor)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'var(--custom-tbl-border)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#1976d2',
                                    },
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                style: {
                                    backgroundColor: 'var(--background-secondary)',
                                    color: 'var(--text-primary)',
                                    padding: '0',
                                    minHeight: '32px',
                                },
                            }}
                        />
                    )}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.code} - {option.title}
                        </li>
                    )}
                    sx={{
                        '& .MuiAutocomplete-listbox': {
                            backgroundColor: 'var(--background-secondary)',
                            color: 'var(--text-primary)',
                        },
                        '& .MuiAutocomplete-option': {
                            '&:hover': {
                                backgroundColor: 'var(--background-primary)',
                            },
                        },
                        '& .MuiAutocomplete-noOptions': {
                            color: 'var(--text-secondary)',
                            padding: '8px 16px',
                            fontSize: '0.875rem'
                        }
                    }}
                />
            </Grid>

            {/* Selected Scenarios Chips */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
                {selectedScenarios.map((scenario) => (
                    <Grid item key={scenario.id}>
                        <Tooltip title={
                            <span className="text-sm">
                                {scenario.code} - {scenario.title}
                            </span>
                        } arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: 'var(--background-secondary)',
                                        borderRadius: '8px',
                                        padding: '8px 12px',
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: 'var(--background-secondary)',
                                    }
                                }
                            }}>
                            <Chip
                                label={scenario.code}
                                onDelete={() => {
                                    setSelectedScenarioIds(prev => prev.filter(id => id !== scenario.id));
                                    setSelectedScenarios(prev => prev.filter(s => s.id !== scenario.id));
                                }}
                                sx={{
                                    background: 'var(--background-secondary)',
                                    color: 'var(--text-primary)',
                                    maxWidth: '110px',
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            />
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>

            {/* Save Button */}
            <Grid container sx={{ mb: 2 }}>
                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSave}
                    sx={{
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                    }}
                >
                    Save
                </Button>
            </Grid>

            {/* Existing Prerequisite Scenarios Table */}
            {preRequisiteScenarioList.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'var(--text-secondary)' }}>
                        Existing Prerequisites
                    </Typography>
                    <TableContainer sx={{
                        backgroundColor: 'var(--background-primary)',
                        '& .MuiTable-root': {
                            tableLayout: 'fixed',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            '& .MuiTableCell-root': {
                                borderColor: 'var(--borderColor)',
                                borderStyle: 'solid',
                                borderWidth: '1px'
                            }
                        }
                    }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{
                                        color: 'var(--text-secondary)',
                                        borderColor: 'var(--borderColor)',
                                        width: '100px',
                                        padding: '8px'
                                    }}>Code</TableCell>
                                    <TableCell sx={{
                                        color: 'var(--text-secondary)',
                                        borderColor: 'var(--borderColor)',
                                        padding: '8px'
                                    }}>Title</TableCell>
                                    <TableCell sx={{
                                        color: 'var(--text-secondary)',
                                        borderColor: 'var(--borderColor)',
                                        width: '50px',
                                        padding: '8px'
                                    }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preRequisiteScenarioList.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell sx={{
                                            color: 'var(--text-primary)',
                                            borderColor: 'var(--borderColor)',
                                            padding: '8px',
                                            width: '100px'
                                        }}>
                                            <Tooltip
                                                title={
                                                    <span className="text-sm">
                                                        {item.test_scenario?.code}
                                                    </span>}
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: 'var(--background-secondary)',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                        },
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: 'var(--background-secondary)',
                                                        }
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {item.test_scenario?.code}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{
                                            color: 'var(--text-primary)',
                                            borderColor: 'var(--borderColor)',
                                            padding: '8px'
                                        }}>
                                            <Tooltip
                                                title={
                                                    <span className="text-sm">
                                                        {item.test_scenario.title}
                                                    </span>
                                                }
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            backgroundColor: 'var(--background-secondary)',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            maxWidth: '500px'
                                                        },
                                                    },
                                                    arrow: {
                                                        sx: {
                                                            color: 'var(--background-secondary)',
                                                        }
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.875rem'
                                                    }}
                                                >
                                                    {item.test_scenario.title}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{
                                            borderColor: 'var(--borderColor)',
                                            padding: '8px',
                                            borderStyle: 'solid',
                                            borderWidth: '1px'
                                        }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuClick(e, item)}
                                                sx={{ color: 'var(--text-secondary)' }}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Menu for actions */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--background-secondary)',
                        color: 'var(--text-primary)',
                    }
                }}
            >
                <MenuItem onClick={handleOpenInNewTab} sx={{ 
                    '&:hover': {
                        backgroundColor: 'var(--hover-bg)'
                    }
                }}>
                    Open
                </MenuItem>
                <MenuItem onClick={handleDetach} sx={{ 
                    '&:hover': {
                        backgroundColor: 'var(--hover-bg)'
                    }
                }}>
                    Detach
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default PrerequisiteScenarios;
